import { IApiErroPadrao, isApiErroPadrao, statusIndicaErroApi } from '@/modelo/ApiErroPadrao';
import MensagemStore from '@/store/MensagemStore';

export type VarianteMensagemApiErro = 'warning' | 'error';
const FLAG_API_ERRO_PADRAO_EXIBIDO = '__apiErroPadraoExibido';
const JANELA_DEDUP_MS = 5000;
const JANELA_DEDUP_ASSINATURA_MS = 1500;
const errosRecentesExibidos = new Map<string, number>();
const origensComApiErroPadraoRecente = new Map<string, number>();

const normalizarTextoChave = (valor?: string): string => {
    return (valor || '').trim().toLowerCase().replace(/\s+/g, ' ');
};

const extrairPathDaOrigem = (origem: any): string => {
    const pathBruto = origem?.response?.config?.url
        || origem?.config?.url
        || origem?.response?.data?.path
        || origem?.path
        || '';

    if (typeof pathBruto !== 'string') {
        return '';
    }

    const pathSemQuery = pathBruto.split('?')[0].trim();
    if (!pathSemQuery) {
        return '';
    }

    try {
        return new URL(pathSemQuery, 'http://localhost').pathname.trim().toLowerCase();
    } catch (_e) {
        return pathSemQuery.toLowerCase();
    }
};

const construirChaveDedupeApiErro = (origem: any, mensagem: string, variante: VarianteMensagemApiErro, status?: number): string => {
    const path = extrairPathDaOrigem(origem);
    return `assinatura:${variante}:${status || ''}:${path}:${normalizarTextoChave(mensagem)}`;
};

const construirChaveOrigem = (origem: any): string => {
    const path = extrairPathDaOrigem(origem);
    return `origem:${path}`;
};

const limparEntradasAntigas = (agora: number): void => {
    for (const [chave, timestamp] of errosRecentesExibidos.entries()) {
        if (agora - timestamp > JANELA_DEDUP_MS) {
            errosRecentesExibidos.delete(chave);
        }
    }

    for (const [chave, timestamp] of origensComApiErroPadraoRecente.entries()) {
        if (agora - timestamp > JANELA_DEDUP_MS) {
            origensComApiErroPadraoRecente.delete(chave);
        }
    }
};

const obterJanelaDedupePorChave = (chave: string): number => {
    if (chave.startsWith('assinatura:')) {
        return JANELA_DEDUP_ASSINATURA_MS;
    }
    return JANELA_DEDUP_MS;
};

const chaveDedupeRecente = (chave: string, agora: number): boolean => {
    const timestamp = errosRecentesExibidos.get(chave);
    if (timestamp === undefined) {
        return false;
    }
    return agora - timestamp <= obterJanelaDedupePorChave(chave);
};

const parseJsonSeguro = (value: string): any => {
    try {
        return JSON.parse(value);
    } catch (_e) {
        return null;
    }
};

const toNumeroOuUndefined = (value: any): number | undefined => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const convertido = Number(value);
        if (!Number.isNaN(convertido)) {
            return convertido;
        }
    }
    return undefined;
};

const toStringOuUndefined = (value: any): string | undefined => {
    if (typeof value === 'string') {
        return value;
    }
    return undefined;
};

const normalizarApiErroPadrao = (candidato: any, statusFallback?: any): IApiErroPadrao | null => {
    if (!candidato || typeof candidato !== 'object') {
        return null;
    }

    // Evita tratar o próprio AxiosError como payload de erro da API.
    if (candidato.isAxiosError || candidato.config || candidato.response) {
        return null;
    }

    const message = toStringOuUndefined(candidato.message);

    const possuiMarcadorBackend =
        candidato.requestId !== undefined
        || candidato.path !== undefined
        || candidato.details !== undefined
        || candidato.timestamp !== undefined
        || statusIndicaErroApi(candidato.status);

    if (!possuiMarcadorBackend) {
        return null;
    }

    const status = toNumeroOuUndefined(statusIndicaErroApi(candidato.status) ? candidato.status : statusFallback);
    const requestId = toStringOuUndefined(candidato.requestId);
    const details = toStringOuUndefined(candidato.details);
    const path = toStringOuUndefined(candidato.path);
    const timestamp = toStringOuUndefined(candidato.timestamp);

    return { message, status, requestId, details, path, timestamp };
};

export const extrairApiErroPadrao = (origem: any): IApiErroPadrao | null => {
    if (!origem) {
        return null;
    }

    if (isApiErroPadrao(origem)) {
        return normalizarApiErroPadrao(origem, origem?.status);
    }

    const data = origem?.response?.data;
    if (isApiErroPadrao(data)) {
        return normalizarApiErroPadrao(data, origem?.response?.status);
    }

    const normalizadoDataObjeto = normalizarApiErroPadrao(data, origem?.response?.status);
    if (normalizadoDataObjeto) {
        return normalizadoDataObjeto;
    }

    if (typeof data === 'string') {
        const dataParseado = parseJsonSeguro(data);
        if (isApiErroPadrao(dataParseado)) {
            return normalizarApiErroPadrao(dataParseado, origem?.response?.status);
        }

        const normalizadoDataString = normalizarApiErroPadrao(dataParseado, origem?.response?.status);
        if (normalizadoDataString) {
            return normalizadoDataString;
        }
    }

    if (typeof origem === 'string') {
        const origemParseada = parseJsonSeguro(origem);
        if (isApiErroPadrao(origemParseada)) {
            return normalizarApiErroPadrao(origemParseada);
        }

        const normalizadoOrigemString = normalizarApiErroPadrao(origemParseada);
        if (normalizadoOrigemString) {
            return normalizadoOrigemString;
        }
    }

    const normalizadoOrigemObjeto = normalizarApiErroPadrao(origem, origem?.response?.status ?? origem?.status);
    if (normalizadoOrigemObjeto) {
        return normalizadoOrigemObjeto;
    }

    return null;
};

export const obterVarianteMensagemApiErro = (status?: number): VarianteMensagemApiErro => {
    if (status && status >= 400 && status <= 499) {
        return 'warning';
    }

    return 'error';
};

export const formatarMensagemApiErroPadrao = (erro: IApiErroPadrao): string => {
    const garantirPontoFinal = (texto?: string): string => {
        const normalizado = texto?.trim() || '';
        if (!normalizado) {
            return '';
        }
        return normalizado.endsWith('.') ? normalizado : `${normalizado}.`;
    };

    const mensagemBase = garantirPontoFinal(erro.message || 'Erro inesperado no servidor');
    const details = garantirPontoFinal(erro.details);
    const requestId = erro.requestId?.trim();
    const partes: string[] = [mensagemBase];

    if (details && details.toLowerCase() !== mensagemBase.toLowerCase()) {
        partes.push(details);
    }

    if (!requestId) {
        return `<span class="mensagem-api-erro-padrao">${partes.join('<br/>')}</span>`;
    }

    partes.push(`<span class="mensagem-request-id">${requestId}</span>`);
    return `<span class="mensagem-api-erro-padrao">${partes.join('<br/>')}</span>`;
};

export interface IApiErroApresentavel {
    mensagem: string;
    variante: VarianteMensagemApiErro;
    erro: IApiErroPadrao;
}

export const construirApiErroApresentavel = (origem: any): IApiErroApresentavel | null => {
    const erroPadrao = extrairApiErroPadrao(origem);
    if (!erroPadrao) {
        return null;
    }

    const status = erroPadrao.status ?? origem?.response?.status ?? origem?.status;

    return {
        erro: erroPadrao,
        mensagem: formatarMensagemApiErroPadrao(erroPadrao),
        variante: obterVarianteMensagemApiErro(status)
    };
};

export interface IOpcoesApresentarApiErro {
    /**
     * Para respostas 2xx: exige assinatura inequívoca de erro (status 4xx/5xx ou message
     * preenchida). Sem isso, um DTO de sucesso com campo homônimo (`path`, `timestamp`)
     * viraria o erro genérico "Erro inesperado no servidor".
     */
    exigirAssinaturaDeErro?: boolean;
}

const possuiAssinaturaDeErro = (erro?: IApiErroPadrao | null): boolean => {
    return !!erro && (statusIndicaErroApi(erro.status) || !!erro.message?.trim());
};

export const apresentarApiErroPadrao = (origem: any, opcoes: IOpcoesApresentarApiErro = {}): boolean => {
    if (origem?.[FLAG_API_ERRO_PADRAO_EXIBIDO]) {
        return true;
    }

    const apiErro = construirApiErroApresentavel(origem);

    if (opcoes.exigirAssinaturaDeErro && !possuiAssinaturaDeErro(apiErro?.erro)) {
        return false;
    }

    const agora = Date.now();
    limparEntradasAntigas(agora);
    const chaveOrigem = construirChaveOrigem(origem);

    if (!apiErro) {
        if (origensComApiErroPadraoRecente.has(chaveOrigem)) {
            marcarApiErroPadraoExibido(origem);
            return true;
        }
        return false;
    }

    const requestId = apiErro.erro.requestId?.trim()?.toLowerCase();
    const status = apiErro.erro.status ?? origem?.response?.status ?? origem?.status;
    const mensagemBaseDedupe = `${apiErro.erro.message || ''} ${apiErro.erro.details || ''}`.trim();
    const chaveAssinatura = construirChaveDedupeApiErro(origem, mensagemBaseDedupe, apiErro.variante, status);
    const chavesDedupe = [chaveAssinatura];
    if (requestId) {
        chavesDedupe.push(`requestId:${requestId}`);
    }
    origensComApiErroPadraoRecente.set(chaveOrigem, agora);

    if (chavesDedupe.some(chave => chaveDedupeRecente(chave, agora))) {
        marcarApiErroPadraoExibido(origem);
        return true;
    }

    chavesDedupe.forEach(chave => errosRecentesExibidos.set(chave, agora));

    if (apiErro.variante === 'warning') {
        MensagemStore.apresentarAviso(apiErro.mensagem);
    } else {
        MensagemStore.apresentarErro(apiErro.mensagem);
    }

    marcarApiErroPadraoExibido(origem);

    return true;
};

export const marcarApiErroPadraoExibido = (origem: any): void => {
    if (origem && typeof origem === 'object') {
        origem[FLAG_API_ERRO_PADRAO_EXIBIDO] = true;
    }
};

export const apiErroPadraoJaExibido = (origem: any): boolean => {
    return !!origem?.[FLAG_API_ERRO_PADRAO_EXIBIDO];
};
